import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, User, Mail, Phone, Building, ChevronRight } from 'lucide-react';
import type { Client } from '../../types';

interface ClientCardProps {
  client: Client;
  onEdit: (client: Client) => void;
  onDelete: (id: string) => void;
}

const ClientCard: React.FC<ClientCardProps> = ({ client, onEdit, onDelete }) => {
  const navigate = useNavigate();

  const handleEdit = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    onEdit(client);
  };

  const handleDelete = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click
    if (window.confirm(`Are you sure you want to delete ${client.name}?`)) {
      onDelete(client.id);
    }
  };

  const handleCardClick = () => {
    navigate(`/clients/${client.id}`);
  };

  return (
    <div 
      className="px-6 py-4 hover:bg-gray-700 transition-colors cursor-pointer"
      onClick={handleCardClick}
    >
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <div className="flex items-center space-x-4">
            <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="font-medium text-white">{client.name}</h3>
              <div className="flex items-center space-x-4 text-sm text-gray-400 mt-1">
                <div className="flex items-center space-x-1">
                  <Mail className="w-3 h-3" />
                  <span>{client.email}</span>
                </div>
                {client.phone && (
                  <div className="flex items-center space-x-1">
                    <Phone className="w-3 h-3" />
                    <span>{client.phone}</span>
                  </div>
                )}
                {client.company && (
                  <div className="flex items-center space-x-1">
                    <Building className="w-3 h-3" />
                    <span>{client.company}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <ChevronRight className="w-4 h-4 text-gray-400" />
          <button
            onClick={handleEdit}
            className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-900 rounded-lg transition-colors"
            title="Edit client"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={handleDelete}
            className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-900 rounded-lg transition-colors"
            title="Delete client"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default ClientCard;
